import { Body, Card, VButton, VInput } from '@vrzl/ui';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import { useUpsertUserAssignment } from '../api/mutations';
import { useAdminPerspectives } from '../api/queries';
import type { Role } from '../api/types';

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'USER', label: 'Gebruiker' },
  { value: 'ADMIN', label: 'Beheerder' },
];

const selectClassName =
  'rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-primary-600';

export function AddUserForm() {
  const perspectivesQuery = useAdminPerspectives();
  const upsert = useUpsertUserAssignment();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('USER');
  const [perspectiveId, setPerspectiveId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const perspectives = perspectivesQuery.data?.items ?? [];

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    if (!email.trim() || !perspectiveId) {
      setFormError('Vul een e-mailadres in en kies een perspectief.');
      return;
    }

    try {
      await upsert.mutateAsync({ email: email.trim(), role, perspectiveId });
      setEmail('');
      setRole('USER');
      setPerspectiveId('');
    } catch (error) {
      console.error(error);
      setFormError('Opslaan is mislukt. Controleer de gegevens en probeer opnieuw.');
    }
  }

  return (
    <Card className="mb-6">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end">
          <div className="flex-1">
            <VInput
              label="E-mail"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="naam@safezone.nl"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Body as="label" htmlFor="add-user-role" weight="medium">
              Rol
            </Body>
            <select
              id="add-user-role"
              className={selectClassName}
              value={role}
              onChange={(event) => setRole(event.target.value as Role)}
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Body as="label" htmlFor="add-user-perspective" weight="medium">
              Perspectief
            </Body>
            <select
              id="add-user-perspective"
              className={selectClassName}
              value={perspectiveId}
              onChange={(event) => setPerspectiveId(event.target.value)}
              disabled={perspectivesQuery.isPending}
            >
              <option value="">Kies een perspectief…</option>
              {perspectives.map((perspective) => (
                <option key={perspective.id} value={perspective.id}>
                  {perspective.name}
                </option>
              ))}
            </select>
          </div>

          <VButton type="submit" icon={Plus} disabled={upsert.isPending}>
            {upsert.isPending ? 'Opslaan…' : 'Toevoegen'}
          </VButton>
        </div>

        {formError && (
          <Body as="span" tone="error">
            {formError}
          </Body>
        )}
      </form>
    </Card>
  );
}
